import { MutationHandlingServiceV2, QueryHandlingService } from '@akebono/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { countries } from 'countries-list';
import { Observable, of } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';

import {
  CurrentUserMinimalGQL,
  CurrentUserMinimalQuery,
  CurrentUserMinimalQueryVariables,
  UserUpdateGQL,
  UserUpdateMutation,
  UserUpdateMutationVariables,
} from '../../graphql/service/graphql-auto-main-service';
import { CountryOption } from './types/country-option.type';

@Component({
  selector: 'app-country-config',
  templateUrl: './country-config.component.html',
  styleUrls: ['./country-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountryConfigComponent {
  readonly countries: CountryOption[];
  readonly countriesMap = countries;

  updating$ = of(false);

  hasEmptyCountryIso$: Observable<boolean>;

  form: FormGroup;

  private countryDetected = false;

  constructor(
    fb: FormBuilder,
    qhs: QueryHandlingService,
    private mhs: MutationHandlingServiceV2,
    private http: HttpClient,
    private currentUserMinimalGQL: CurrentUserMinimalGQL,
    private userUpdateGQL: UserUpdateGQL,
    private cdr: ChangeDetectorRef,
  ) {
    this.form = fb.group({
      country: [null, Validators.required],
    });

    this.countries = Object.entries(countries)
      .map(([code, country]) => ({
        code,
        name: country.name,
        emoji: country.emoji,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const currentUserRef = qhs.watch<CurrentUserMinimalQuery, CurrentUserMinimalQueryVariables>(
      this.currentUserMinimalGQL,
    );

    this.hasEmptyCountryIso$ = currentUserRef.data.pipe(
      map((data) => this.isCountryIsoEmpty(data?.currentUser?.countryIso)),
      tap((hasEmptyCountryIso) => {
        if (hasEmptyCountryIso) {
          this.detectCountry();
        }
      }),
      shareReplay(1),
    );
  }

  updateCountryIso(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      return;
    }

    const countryIso = this.form.controls.country.value?.toLowerCase();

    this.updating$ = this.mhs.mutate<UserUpdateMutation, UserUpdateMutationVariables>(
      this.userUpdateGQL,
      {
        input: {
          countryIso,
        },
      },
      { refetch: true },
    ).loading;
  }

  private detectCountry(): void {
    if (this.countryDetected) {
      return;
    }

    this.countryDetected = true;

    this.http
      .get<{ country?: string }>('https://locate.akebono.world/country')
      .pipe(take(1))
      .subscribe((res) => {
        const detectedCountry = res?.country?.toUpperCase();

        if (detectedCountry && this.countriesMap[detectedCountry]) {
          this.form.controls.country.setValue(detectedCountry);
          this.cdr.markForCheck();
        }
      });
  }

  private isCountryIsoEmpty(countryIso: string | null | undefined): boolean {
    return !countryIso || countryIso.trim() === '';
  }
}
